import { useMemo, useState } from 'react';
import useTable from '@/hooks/useTable';
import { ErrorBlock, LoadingBlock } from '@/components/feature/SectionState';
import type { GsEvent } from '@/lib/types';
import { monthKey } from '@/lib/format';
import TournamentCard from './TournamentCard';
import CalendarMonthGrid from './CalendarMonthGrid';

type View = 'list' | 'calendar';

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3.5 py-1.5 rounded-full text-sm font-label font-semibold uppercase tracking-wide whitespace-nowrap cursor-pointer border transition-colors ${active ? 'bg-foreground-950 text-background-50 border-foreground-950' : 'bg-background-50 text-foreground-800 border-background-300 hover:border-foreground-700'}`}
    >
      {label}
    </button>
  );
}

export default function TournamentBrowser() {
  const { data: events, loading, error, reload } = useTable<GsEvent>('gs_events');
  const [view, setView] = useState<View>('list');
  const [county, setCounty] = useState('All');
  const [type, setType] = useState('All');
  const [month, setMonth] = useState('All');

  const visible = useMemo(() => events.filter((e) => e.show_in_calendar !== false), [events]);
  const counties = useMemo(() => Array.from(new Set(visible.map((e) => e.county).filter(Boolean) as string[])), [visible]);
  const types = useMemo(() => Array.from(new Set(visible.map((e) => e.event_type))), [visible]);
  const months = useMemo(() => {
    const set = new Set<string>();
    visible.forEach((e) => { const k = monthKey(e.event_date); if (k) set.add(k); });
    return Array.from(set).sort((a, b) => new Date(`1 ${a}`).getTime() - new Date(`1 ${b}`).getTime());
  }, [visible]);

  const list = useMemo(
    () => visible
      .filter((e) => (county === 'All' || e.county === county) && (type === 'All' || e.event_type === type) && (month === 'All' || monthKey(e.event_date) === month))
      .sort((a, b) => (a.event_date || '').localeCompare(b.event_date || '')),
    [visible, county, type, month],
  );

  const groups = useMemo(() => {
    const map = new Map<string, GsEvent[]>();
    list.forEach((e) => {
      const key = monthKey(e.event_date) || `${e.month_label} ${e.year_label}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    });
    return Array.from(map.entries());
  }, [list]);

  return (
    <section id="calendar" className="bg-background-50 px-4 md:px-8 py-14 md:py-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="font-heading font-extrabold uppercase text-3xl md:text-5xl text-foreground-950 leading-none">Full schedule</h2>
          <p className="mt-2 text-foreground-600 text-sm">Filter by county, date and type — or switch to the calendar view.</p>
        </div>
        <div className="flex items-center gap-3">
          <p className="font-label uppercase tracking-widest text-xs text-foreground-500 whitespace-nowrap">
            {loading ? 'Loading' : `${list.length} tournament${list.length === 1 ? '' : 's'}`}
          </p>
          <div className="flex items-center px-1 py-1 rounded-full border border-background-300 bg-background-100">
            <button type="button" onClick={() => setView('list')} className={`px-3.5 py-1.5 rounded-full font-label font-semibold uppercase tracking-wide text-xs whitespace-nowrap cursor-pointer transition-colors ${view === 'list' ? 'bg-foreground-950 text-background-50' : 'text-foreground-700'}`}>List</button>
            <button type="button" onClick={() => setView('calendar')} className={`px-3.5 py-1.5 rounded-full font-label font-semibold uppercase tracking-wide text-xs whitespace-nowrap cursor-pointer transition-colors ${view === 'calendar' ? 'bg-foreground-950 text-background-50' : 'text-foreground-700'}`}>Calendar</button>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 mb-10">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-label uppercase text-xs tracking-widest text-foreground-600 w-16">County</span>
          {['All', ...counties].map((c) => <Chip key={c} label={c === 'All' ? 'All counties' : `${c} County`} active={county === c} onClick={() => setCounty(c)} />)}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-label uppercase text-xs tracking-widest text-foreground-600 w-16">Type</span>
          {['All', ...types].map((t) => <Chip key={t} label={t} active={type === t} onClick={() => setType(t)} />)}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-label uppercase text-xs tracking-widest text-foreground-600 w-16">Date</span>
          <div className="relative">
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="appearance-none text-sm border border-background-300 rounded-md pl-3 pr-9 py-1.5 bg-background-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <option value="All">All dates</option>
              {months.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none text-foreground-600"><i className="ri-arrow-down-s-line"></i></span>
          </div>
        </div>
      </div>

      {loading && <LoadingBlock label="Loading tournaments" />}
      {error && <ErrorBlock message={error} onRetry={reload} />}

      {!loading && !error && list.length === 0 && (
        <p className="py-16 text-center text-foreground-600">No tournaments match these filters yet. Try widening your search.</p>
      )}

      {!loading && !error && list.length > 0 && view === 'calendar' && (
        <CalendarMonthGrid events={list} />
      )}

      {!loading && !error && list.length > 0 && view === 'list' && (
        <div className="space-y-12">
          {groups.map(([groupMonth, items]) => (
            <div key={groupMonth}>
              <div className="flex items-baseline gap-4 mb-5 border-b-2 border-foreground-950 pb-2">
                <h3 className="font-heading font-extrabold uppercase text-2xl md:text-3xl text-primary-500 leading-none">{groupMonth}</h3>
                <span className="font-label uppercase tracking-widest text-[11px] text-foreground-500">{items.length} tournament{items.length === 1 ? '' : 's'}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {items.map((e) => <TournamentCard key={e.id} event={e} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}